import type { DBIssue } from '../trpc-types/daily-brief-trpc-types';

// ---------------------------------------------------------------------------
// Deterministic one-line evidence string for each Issue card. Built purely from
// raw DBIssue wire values, never the LLM narrative (carried separately as
// `whyNow`), so the card renders even with no narrative attached. All
// presentation math (percent, rounding, currency, compact units) lives here;
// the wire stays raw.
// ---------------------------------------------------------------------------

const NA = 'n/a';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

const compact = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1
});

function pct(ratio: number | null): string {
  return ratio === null ? NA : `${Math.round(ratio * 100)}%`;
}

function money(value: number | null): string {
  return value === null ? NA : usd.format(value);
}

// Compact integer: 4,100,000 -> "4.1M".
function units(value: number | null): string {
  return value === null ? NA : compact.format(value);
}

function days(value: number | null): string {
  return value === null ? NA : `${value}d`;
}

function date(iso: string | null): string {
  return iso === null ? NA : iso.slice(0, 10);
}

// Unit-cost as a multiple of plan (actual / planned); n/a when planned is 0 or null.
function multiple(actual: number | null, planned: number | null): string {
  if (actual === null || planned === null || planned === 0) return NA;
  return `${(actual / planned).toFixed(1)}×`;
}

/**
 * Format the Evidence Line for one Issue. Exhaustive over the DBIssue
 * discriminated union: each variant reads only the raw metrics its card needs.
 * Null metrics, percents, currency, and dates degrade to "n/a" rather than
 * throwing, so a sparse Cube row still renders.
 */
export function evidenceLine(issue: DBIssue): string {
  switch (issue.code) {
    case 'FAILURE_TO_LAUNCH':
      return `Live since ${date(issue.startDate)} (${days(issue.daysLive)}) — $0 of ${money(
        issue.mediaBudget
      )} spent, 0 units`;
    case 'CRITICAL_UNDERDELIVERY':
      return `Delivery pacing ${pct(issue.deliveryPacing)} with ${pct(
        issue.scheduleElapsed
      )} of duration elapsed — ${units(issue.unitsRemaining)} units behind`;
    case 'RUNAWAY_SPEND':
      return `Spend pacing ${pct(issue.spendPacing)} vs delivery ${pct(
        issue.deliveryPacing
      )} — unit cost ${multiple(issue.unitCostActual, issue.plannedUnitCost)} plan`;
    case 'CRITICAL_FAILURE':
      return `Delivery pacing ${pct(issue.deliveryPacing)} and margin ${pct(
        issue.marginActual
      )} vs ${pct(issue.plannedMargin)} planned, with ${pct(
        issue.scheduleElapsed
      )} of duration elapsed`;
    case 'NOT_RUNNING':
      return `Stalled ${days(issue.stallDays)} (last active ${date(
        issue.lastActivityDate
      )}) — ${money(issue.budgetRemaining)} / ${units(issue.unitsRemaining)} units left`;
    case 'UNDERDELIVERY_RISK':
      return `Delivery pacing ${pct(issue.deliveryPacing)} with ${pct(
        issue.scheduleElapsed
      )} of duration elapsed — recoverable`;
    case 'MARGIN_EROSION':
      return `On pace but margin ${pct(issue.marginActual)} vs ${pct(
        issue.plannedMargin
      )} planned — unit cost ${multiple(issue.unitCostActual, issue.plannedUnitCost)} plan`;
    case 'OVERDELIVERY_OVERSPEND':
      return `Delivering ${pct(issue.deliveryPacing)} and spending ${pct(
        issue.spendPacing
      )} of plan — over-pacing on both`;
    case 'APPROACHING_REVENUE_CAP':
      return `Pacing ${pct(issue.deliveryPacing)} with ${days(
        issue.daysRemaining
      )} left — on track to hit contracted units early`;
    default: {
      const _exhaustive: never = issue;
      return _exhaustive;
    }
  }
}
